import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPost } from '../utils/api';
import styles from './ExploreUsersPage.module.css';

const ExploreUsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [friendRequestLoading, setFriendRequestLoading] = useState({});

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiGet('/friendships/users');
        
        if (response.ok) {
          const usersData = await response.json();
          console.log('All users from API:', usersData);
          console.log('Current user email:', user?.email);
          // Filter out the current user
          const filteredUsers = usersData.filter(u => u.email !== user?.email);
          console.log('Filtered users:', filteredUsers);
          setUsers(filteredUsers);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to load users');
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsers();
  }, [user]);

  const handleSearch = async (searchValue) => {
    if (!searchValue.trim()) {
      setIsSearching(false);
      // Reload all users when search is empty
      try {
        setLoading(true);
        setError('');
        const response = await apiGet('/friendships/users');
        
        if (response.ok) {
          const usersData = await response.json();
          // Filter out the current user
          const filteredUsers = usersData.filter(u => u.email !== user?.email);
          setUsers(filteredUsers);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to load users');
          setUsers([]);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Network error. Please check your connection and try again.');
        setUsers([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setSearchLoading(true);
      setError('');
      const response = await apiGet(`/friendships/search?name=${encodeURIComponent(searchValue)}`);
      
      if (response.ok) {
        const searchResults = await response.json();
        console.log('Search results from API:', searchResults);
        console.log('Current user ID:', user?.id);
        // Filter out the current user
        const filteredResults = searchResults.filter(u => u.email !== user?.email);
        console.log('Filtered search results:', filteredResults);
        setUsers(filteredResults);
        setIsSearching(true);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Search failed');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setError('Search error. Please try again.');
      setUsers([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  const clearSearch = async () => {
    setSearchTerm('');
    setIsSearching(false);
    
    // Reload all users from API
    try {
      setLoading(true);
      setError('');
      const response = await apiGet('/friendships/users');
      
      if (response.ok) {
        const usersData = await response.json();
        console.log('Clear search - All users from API:', usersData);
        console.log('Current user ID:', user?.id);
        // Filter out the current user
        const filteredUsers = usersData.filter(u => u.email !== user?.email);
        console.log('Clear search - Filtered users:', filteredUsers);
        setUsers(filteredUsers);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to load users');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Network error. Please check your connection and try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };


  const handleSendFriendRequest = async (userId) => {
    setFriendRequestLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await apiPost('/friendships/send-request', {
        receiver_id: userId
      });

      if (response.ok) {
        // Update the user's friendship status in the local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, friendship_status: 'pending_sent' }
              : user
          )
        );
      } else {
        const errorData = await response.json();
        console.error('Failed to send friend request:', errorData);
        // You could add a toast notification here
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
    } finally {
      setFriendRequestLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  if (loading) {
    return (
      <div className={styles.exploreUsersPage}>
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  if (error && !isSearching) {
    return (
      <div className={styles.exploreUsersPage}>
        <header className={styles.exploreHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerText}>
              <h1>Explore Users</h1>
              <p>Find and connect with other users</p>
            </div>
            <Link to="/friends" className={`btn btn-secondary ${styles.backButton}`}>
              Back to Friends
            </Link>
          </div>
        </header>
        <div className="error-state">
          <h2>Error Loading Users</h2>
          <p>{error}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.exploreUsersPage}>
      <header className={styles.exploreHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <h1>Explore Users</h1>
            <p>Find and connect with other users</p>
          </div>
          <Link to="/friends" className={`btn btn-secondary ${styles.backButton}`}>
            Back to Friends
          </Link>
        </div>
      </header>

      <div className={styles.searchSection}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search users by name..."
            value={searchTerm}
            onChange={handleSearchInputChange}
            className={styles.searchInput}
          />
          {searchLoading && (
            <div className={styles.searchLoading}>Searching...</div>
          )}
        </div>
        {isSearching && (
          <button 
            onClick={clearSearch}
            className={`btn btn-secondary ${styles.clearButton}`}
          >
            Clear Search
          </button>
        )}
      </div>

      <div className={styles.usersList}>
        {users.length === 0 ? (
          <div className="empty-state">
            <h3>
              {isSearching ? 'No users found' : 'No users available'}
            </h3>
            <p>
              {isSearching 
                ? 'Try searching with a different name' 
                : 'There are no other users to explore at the moment'
              }
            </p>
          </div>
        ) : (
          users.map(user => (
            <div key={user.id} className={styles.userCard}>
              <div className={styles.userInfo}>
                <h3>{user.first_name} {user.last_name}</h3>
              </div>
              <div className={styles.userActions}>
                <Link 
                  to={`/user/${user.id}`} 
                  className={`btn btn-secondary ${styles.profileButton}`}
                >
                  View Profile
                </Link>
                {user.friendship_status === 'pending_sent' ? (
                  <span className={styles.requestSentBadge}>
                    Request Sent
                  </span>
                ) : user.friendship_status === 'accepted' ? (
                  <span className={styles.friendBadge}>
                    Friends
                  </span>
                ) : user.friendship_status === 'pending_received' ? (
                  <span className={styles.pendingReceivedBadge}>
                    Request Received
                  </span>
                ) : (
                  <button 
                    className={`btn btn-primary ${styles.friendRequestButton}`}
                    onClick={() => handleSendFriendRequest(user.id)}
                    disabled={friendRequestLoading[user.id]}
                  >
                    {friendRequestLoading[user.id] ? 'Sending...' : 'Send Friend Request'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ExploreUsersPage;
